import cx from "ui/cx";
import IconInfoLine from "ui/icons/IconInfoLine";
import type { WithTooltipProps } from "ui/popovers/Tooltip";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./Label.module.scss";

// Base UI has no standalone Label primitive (it offers `Field.Label`); render a native <label>.
export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement>, WithTooltipProps {
  required?: boolean;
  disabled?: boolean;
}

export default function Label({
  children,
  className,
  style,
  tooltip,
  required = false,
  disabled = false,
  htmlFor,
  ...props
}: LabelProps) {
  // The row is the outer element, so a consumer `className` lands on the box that owns layout.
  // Typography lives here too and reaches the text by inheritance; declaring it on the <label>
  // instead would beat any inherited value a consumer set out here. `style` rides along with
  // `className` so the pair addresses the same element; the rest of the props stay on the <label>,
  // which is where `htmlFor` and the label's own handlers belong.
  return (
    <span
      className={cx(styles.root, disabled && styles.disabled, htmlFor && styles.clickable, className)}
      style={style}
    >
      {/* The tooltip icon is a sibling of the <label>, never a descendant. An accessible name on a
          descendant joins the label's own name computation, so a Switch labelled "Tag" would answer
          to "Tag More information". Sitting outside also means a click on the icon is not label
          activation, so the `htmlFor` control does not toggle and needs no guard to stop it. */}
      <label
        className={styles.label}
        data-testid="label-element"
        htmlFor={!disabled ? htmlFor : undefined}
        data-disabled={disabled}
        {...props}
      >
        <span className={styles.content}>
          {children}
          {required && <span className={styles.requiredIndicator}>*</span>}
        </span>
      </label>
      {tooltip &&
        wrapWithTooltip(
          // Not a button and not focusable: consumers put Label inside interactive elements
          // (an Accordion.Trigger is itself a button), and nesting one button in another is
          // invalid markup that also hands the outer control the icon's clicks. `stopPropagation`
          // keeps a click on the icon from reaching that outer control.
          <span className={styles.tooltipIconWrapper} aria-hidden onClick={(event) => event.stopPropagation()}>
            <IconInfoLine className={styles.tooltipIcon} />
          </span>,
          tooltip,
        )}
    </span>
  );
}
